import { useCustomToast } from '@/shared/lib';
import {
  createSettingsManagement,
  exposeSettingsManagementReads,
  resolveSettingsRejectionMessage,
  runWithToast,
} from './settings-management.factory';
import { SETTINGS_TOAST_MESSAGES } from './settings-messages';
import {
  useIndexingStore,
  useProtectionMeansStore,
  useRecordOriginStore,
  useRecordStageStore,
  useRecordTypeStore,
} from '../model/settings-stores';
import type { RecordOriginPayload } from '../model/settings.types';

export const useIndexingManagement = createSettingsManagement(useIndexingStore);
export const useRecordTypeManagement = createSettingsManagement(useRecordTypeStore);
export const useProtectionMeansManagement = createSettingsManagement(useProtectionMeansStore);
export const useRecordStageManagement = createSettingsManagement(useRecordStageStore);

export function useRecordOriginManagement() {
  const store = useRecordOriginStore();
  const toast = useCustomToast();

  const reads = exposeSettingsManagementReads(store);
  const rejectionMessage = () => resolveSettingsRejectionMessage(store);

  async function add(payload: RecordOriginPayload): Promise<boolean> {
    return runWithToast(
      toast,
      () => store.create(payload),
      { success: SETTINGS_TOAST_MESSAGES.addSuccess, error: SETTINGS_TOAST_MESSAGES.addError },
      rejectionMessage
    );
  }

  async function edit(id: number, payload: RecordOriginPayload): Promise<boolean> {
    return runWithToast(
      toast,
      () => store.update(id, payload),
      { success: SETTINGS_TOAST_MESSAGES.editSuccess, error: SETTINGS_TOAST_MESSAGES.editError },
      rejectionMessage
    );
  }

  async function remove(id: number): Promise<boolean> {
    return runWithToast(toast, () => store.remove(id), {
      success: SETTINGS_TOAST_MESSAGES.deleteSuccess,
      error: SETTINGS_TOAST_MESSAGES.deleteError,
    });
  }

  return { ...reads, add, edit, remove };
}
